import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import type { AxiosResponse } from 'axios'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'
import {
	type HttpUpdateServiceVettingGalleryImagesPositionsOptions,
	httpUpdateServiceVettingGalleryImagesPositions
} from '../http/services-vetting.http'
import type { ServiceProPhotos } from '../types'

export const useUpdateServiceVettingGalleryImagesPositions = (
	urlParams: HttpUpdateServiceVettingGalleryImagesPositionsOptions['urlParams'],
	options?: UseMutationOptions<
		AxiosResponse<unknown>,
		unknown,
		HttpUpdateServiceVettingGalleryImagesPositionsOptions['payload']
	>
) => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (payload) =>
			httpUpdateServiceVettingGalleryImagesPositions({
				urlParams,
				payload,
				params: {
					responseType: 'small'
				}
			}),
		onError: async (error, variables, onMutateResult, context) => {
			const queryKey = [
				QUERY_KEYS.SERVICE_PRO_PHOTOS,
				urlParams.serviceId
			]
			const previousData = await queryClient.getQueryData(queryKey)
			await queryClient.cancelQueries({ queryKey })
			queryClient.setQueriesData({ queryKey }, previousData)
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onMutate: async (variables) => {
			const queryKey = [
				QUERY_KEYS.SERVICE_PRO_PHOTOS,
				urlParams.serviceId
			]

			// Cancel any outgoing refetches
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous values
			const previousData =
				queryClient.getQueryData<ServiceProPhotos>(queryKey)

			// Update cached data with new positions
			if (previousData) {
				const updatedImages = previousData.gallery.images

				// Create a map of id to new position
				const positionMap = variables.reduce(
					(acc, item) => {
						acc[item.id] = item.position
						return acc
					},
					{} as Record<number, number>
				)

				// Sort images based on new positions
				updatedImages.sort((a, b) => {
					const posA = positionMap[a.id]
					const posB = positionMap[b.id]
					return posA - posB
				})

				queryClient.setQueriesData<ServiceProPhotos>(
					{ queryKey },
					{
						...previousData,
						gallery: {
							...previousData.gallery,
							images: updatedImages
						}
					}
				)
			}

			return previousData
		},
		onSuccess: (data, variables, onMutateResult, context) => {
			queryClient.invalidateQueries({
				queryKey: [
					QUERY_KEYS.SERVICE_GALLERY_IMAGES_PRO_PHOTOS,
					urlParams.serviceId
				]
			})
			options?.onSuccess?.(data, variables, onMutateResult, context)
		},
		...options
	})
}
